const DEFAULT_SWIPE_THRESHOLD = 40;
// After a swipe, clicks within this window (ms) are the drag's own, not a new tap.
const SWIPE_CLICK_WINDOW = 400;
// Pixels the finger must travel before we decide whether the gesture is horizontal or vertical.
const AXIS_LOCK_DISTANCE = 8;

/**
 * Native carousel API (no Embla).
 *
 * @param {HTMLElement} root
 * @param {Object} options
 * @returns {{scrollTo: Function, scrollPrev: Function, scrollNext: Function, getIndex: Function, getSlideCount: Function, setAllowSwipe: Function, reInit: Function, destroy: Function}|null}
 */
export function createCarousel(root, options)
{
    if (!root || root.classList.contains('is-initialized')) {
        return null;
    }

    const config = Object.assign({
        selectors: {},
        loop: false,
        loopStrategy: 'ends', // 'ends' | 'set'
        mode: 'percent', // 'percent' | 'offset'
        slidesPerView: 1, // number | 'css'
        slidesPerGroup: 1, // number | 'css' | function
        startIndex: 0,
        swipe: true,
        swipeThreshold: DEFAULT_SWIPE_THRESHOLD,
        suppressClickOnSwipe: true,
        arrows: {
            mode: 'toggle-hidden' // 'toggle-hidden' | 'disable' | 'hide-mobile-toggle-desktop'
        },
        watchResize: false,
        onSelect: null,
        onInit: null,
        thumbnails: null,
        excludeCloneFromSlides: true
    }, options || {});

    const selectors = Object.assign({
        viewport: null,
        track: null,
        slide: null,
        prev: null,
        next: null,
        pagination: null,
        paginationItem: '[data-slide-index]'
    }, config.selectors || {});

    const track = selectors.track ? root.querySelector(selectors.track) : null;
    const viewport = selectors.viewport ? root.querySelector(selectors.viewport) : root;
    const prevButton = selectors.prev ? root.querySelector(selectors.prev) : null;
    const nextButton = selectors.next ? root.querySelector(selectors.next) : null;
    const paginator = selectors.pagination ? root.querySelector(selectors.pagination) : null;

    if (!track || !selectors.slide) {
        return null;
    }

    let realSlides = Array.from(track.querySelectorAll(selectors.slide));
    let slideCount = realSlides.length;

    if (slideCount === 0) {
        return null;
    }

    let allSlides = realSlides.slice();
    let currentIndex = clamp(config.startIndex, 0, slideCount - 1);
    let position = currentIndex;
    let hasLoop = false;
    let isAnimating = false;
    let animationUnlockTimer = null;
    let allowSwipe = Boolean(config.swipe);
    let touchStartX = 0;
    let touchStartY = 0;
    let touchDeltaX = 0;
    let swipeAxis = null; // null until decided, then 'x' | 'y'
    let isSwiping = false;
    // Time until which a click is treated as the tail of a swipe (see onClickCapture).
    let suppressClickUntil = 0;
    let dragByMouse = false;
    let destroyed = false;
    let resizeObserver = null;
    let resizeFrame = null;

    function clearAnimationUnlockTimer()
    {
        if (animationUnlockTimer) {
            clearTimeout(animationUnlockTimer);
            animationUnlockTimer = null;
        }
    }

    /**
     * omitEnd can produce the same translate for consecutive indexes; without a
     * transform change, transitionend never fires and swipe stays locked.
     */
    function setAnimating(active)
    {
        isAnimating = Boolean(active);
        clearAnimationUnlockTimer();
        if (!isAnimating || destroyed) {
            return;
        }
        animationUnlockTimer = window.setTimeout(function() {
            animationUnlockTimer = null;
            if (!isAnimating || destroyed) {
                return;
            }
            const previousIndex = currentIndex;
            normalizePosition();
            isAnimating = false;
            track.classList.remove('is-dragging');
            updateButtons();
            if (previousIndex !== currentIndex) {
                emitSelect(previousIndex);
            }
        }, 600);
    }

    const thumbs = resolveThumbnails(root, config.thumbnails);

    function resolveLoop()
    {
        if (typeof config.loop === 'function') {
            return Boolean(config.loop(slideCount));
        }
        return Boolean(config.loop) && slideCount > 1;
    }

    function getSlidesPerView()
    {
        if (config.slidesPerView === 'css') {
            const value = parseFloat(getComputedStyle(root).getPropertyValue('--slides-per-view'));
            return Number.isFinite(value) && value > 0 ? value : 1;
        }
        return Number(config.slidesPerView) > 0 ? Number(config.slidesPerView) : 1;
    }

    function getSlidesPerGroup()
    {
        let value = config.slidesPerGroup;
        if (value === 'css') {
            return Math.max(1, Math.floor(getSlidesPerView()));
        }
        if (typeof value === 'function') {
            value = value();
        }
        value = Number(value);
        return Number.isFinite(value) && value > 0 ? Math.floor(value) : 1;
    }

    function getPageStarts()
    {
        const maxIndex = getMaxIndex();
        const step = Math.max(1, getSlidesPerGroup());
        const pages = [];

        for (let index = 0; index < maxIndex; index += step) {
            pages.push(index);
        }

        if (!pages.length || pages[pages.length - 1] !== maxIndex) {
            pages.push(maxIndex);
        }

        return pages;
    }

    function getPageIndexForPosition(positionIndex)
    {
        const pages = getPageStarts();
        let pageIndex = 0;
        for (let index = 0; index < pages.length; index++) {
            if (pages[index] <= positionIndex) {
                pageIndex = index;
            }
        }
        return pageIndex;
    }

    function getMaxScroll()
    {
        if (config.mode !== 'offset' || !viewport) {
            return 0;
        }

        const viewportWidth = viewport.offsetWidth;
        if (viewportWidth <= 0) {
            return 0;
        }

        const slides = getRealSlides();
        let trackWidth = track.scrollWidth;
        if (trackWidth <= viewportWidth + 1) {
            trackWidth = slides.reduce(function(total, slide) {
                return total + slide.offsetWidth;
            }, 0);
        }

        return Math.max(0, trackWidth - viewportWidth);
    }

    function getMaxIndex()
    {
        if (config.mode === 'offset') {
            const slides = getRealSlides();
            if (!slides.length || !viewport) {
                return 0;
            }

            const viewportWidth = viewport.offsetWidth;
            if (viewportWidth <= 0) {
                // Offcanvas/layout not measurable yet: stay at start (omitEnd).
                return 0;
            }

            const maxScroll = getMaxScroll();
            if (maxScroll <= 1) {
                return 0;
            }

            let measured = false;
            let maxIndex = 0;
            for (let index = 0; index < slides.length; index++) {
                const offsetLeft = slides[index].offsetLeft;
                if (offsetLeft > 0) {
                    measured = true;
                }
                maxIndex = index;
                // First slide that reaches/past the end fill point (omitEnd).
                if (offsetLeft >= maxScroll - 1) {
                    break;
                }
            }

            // Offsets still collapsed (e.g. images sizing): do not jump to last slide.
            if (!measured && slides.length > 1) {
                return 0;
            }

            return maxIndex;
        }

        return Math.max(0, slideCount - Math.ceil(getSlidesPerView()));
    }

    function getRealSlides()
    {
        return Array.from(track.querySelectorAll(selectors.slide + ':not(.is-clone)'));
    }

    function clearClones()
    {
        track.querySelectorAll(selectors.slide + '.is-clone').forEach(function(clone) {
            clone.remove();
        });
    }

    function createClone(slide)
    {
        const clone = slide.cloneNode(true);
        clone.classList.add('is-clone');
        clone.setAttribute('aria-hidden', 'true');
        return clone;
    }

    function setupClones()
    {
        clearClones();
        realSlides = getRealSlides();
        slideCount = realSlides.length;
        hasLoop = resolveLoop();

        if (!hasLoop) {
            allSlides = realSlides.slice();
            position = currentIndex;
            return;
        }

        if (config.loopStrategy === 'set') {
            realSlides.forEach(function(slide) {
                track.appendChild(createClone(slide));
            });

            for (let index = realSlides.length - 1; index >= 0; index--) {
                track.insertBefore(createClone(realSlides[index]), track.firstChild);
            }

            allSlides = Array.from(track.querySelectorAll(selectors.slide));
            position = slideCount + currentIndex;
            return;
        }

        // loopStrategy: 'ends'
        track.appendChild(createClone(realSlides[0]));
        track.insertBefore(createClone(realSlides[slideCount - 1]), realSlides[0]);
        allSlides = Array.from(track.querySelectorAll(selectors.slide));
        position = currentIndex + 1;
    }

    function getOffsetForPosition(pos)
    {
        if (config.mode === 'offset') {
            const slide = allSlides[pos];
            if (!slide) {
                return 0;
            }
            // omitEnd: never scroll past the point where the track end fills the viewport.
            return -Math.min(slide.offsetLeft, getMaxScroll());
        }

        const step = 100 / getSlidesPerView();
        return -(pos * step);
    }

    function setTransform(pos, instant, dragOffset)
    {
        let offset = getOffsetForPosition(pos);
        const unit = config.mode === 'offset' ? 'px' : '%';

        if (typeof dragOffset === 'number' && dragOffset !== 0) {
            if (config.mode === 'offset') {
                offset += dragOffset;
                // Keep drag within omitEnd bounds (no blank space on the right).
                const maxScroll = getMaxScroll();
                offset = Math.max(-maxScroll, Math.min(0, offset));
            } else {
                const width = (viewport && viewport.offsetWidth) || track.offsetWidth || 1;
                offset += (dragOffset / width) * 100;
            }
        }

        const isDrag = typeof dragOffset === 'number' && dragOffset !== 0;

        if (instant || isDrag) {
            track.classList.add('is-dragging');
        }

        track.style.transform = 'translate3d(' + offset + unit + ', 0px, 0px)';

        if (instant && !isDrag) {
            void track.offsetHeight;
            track.classList.remove('is-dragging');
        }
    }

    function updatePagination(previousIndex, nextIndex)
    {
        if (!paginator) {
            return;
        }

        const previous = paginator.querySelector(selectors.paginationItem + '[data-slide-index="' + previousIndex + '"]');
        const next = paginator.querySelector(selectors.paginationItem + '[data-slide-index="' + nextIndex + '"]');

        if (previous) {
            previous.classList.remove('bg-black');
            previous.classList.add('bg-gray-light');
        }

        if (next) {
            next.classList.remove('bg-gray-light');
            next.classList.add('bg-black');
        }
    }

    function updateThumbnails()
    {
        if (!thumbs.length) {
            return;
        }

        thumbs.forEach(function(thumb, index) {
            thumb.classList.toggle('active', index === currentIndex);
        });

        const activeThumb = thumbs[currentIndex];
        if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
            activeThumb.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'});
        }
    }

    function isMobileWidth()
    {
        return window.innerWidth < 768;
    }

    function updateButtons()
    {
        if (!prevButton && !nextButton) {
            return;
        }

        const mode = (config.arrows && config.arrows.mode) || 'toggle-hidden';
        const maxIndex = getMaxIndex();
        const canPrev = hasLoop ? slideCount > 1 : currentIndex > 0;
        const canNext = hasLoop ? slideCount > 1 : currentIndex < maxIndex;

        if (mode === 'hide-mobile-toggle-desktop') {
            if (isMobileWidth()) {
                if (prevButton) prevButton.classList.add('d-none');
                if (nextButton) nextButton.classList.add('d-none');
                return;
            }

            if (prevButton) prevButton.classList.toggle('d-none', !canPrev);
            if (nextButton) nextButton.classList.toggle('d-none', !canNext);
            return;
        }

        if (mode === 'disable') {
            if (prevButton) {
                prevButton.classList.toggle('d-none', slideCount <= 1);
                prevButton.disabled = !canPrev;
            }
            if (nextButton) {
                nextButton.classList.toggle('d-none', slideCount <= 1);
                nextButton.disabled = !canNext;
            }
            return;
        }

        // toggle-hidden
        if (slideCount <= 1 && !hasLoop) {
            if (prevButton) prevButton.classList.add('d-none');
            if (nextButton) nextButton.classList.add('d-none');
            return;
        }

        if (prevButton) prevButton.classList.toggle('d-none', !canPrev);
        if (nextButton) nextButton.classList.toggle('d-none', !canNext);
    }

    function normalizePosition()
    {
        if (!hasLoop) {
            currentIndex = position;
            return;
        }

        if (config.loopStrategy === 'set') {
            if (position < slideCount) {
                position += slideCount;
                setTransform(position, true);
            } else if (position >= slideCount * 2) {
                position -= slideCount;
                setTransform(position, true);
            }
            currentIndex = position - slideCount;
            return;
        }

        // ends
        if (position === 0) {
            position = slideCount;
            currentIndex = slideCount - 1;
            setTransform(position, true);
        } else if (position === slideCount + 1) {
            position = 1;
            currentIndex = 0;
            setTransform(position, true);
        } else {
            currentIndex = position - 1;
        }
    }

    function emitSelect(previousIndex)
    {
        updatePagination(previousIndex, currentIndex);
        updateThumbnails();
        if (typeof config.onSelect === 'function') {
            config.onSelect(currentIndex, previousIndex);
        }
    }

    function scrollTo(index, instant)
    {
        if (destroyed || slideCount === 0) {
            return;
        }

        const previousIndex = currentIndex;
        const maxIndex = getMaxIndex();
        const previousTransform = track.style.transform;

        if (hasLoop) {
            currentIndex = ((index % slideCount) + slideCount) % slideCount;
            position = config.loopStrategy === 'set'
                ? slideCount + currentIndex
                : currentIndex + 1;
        } else {
            currentIndex = clamp(index, 0, maxIndex);
            position = currentIndex;
        }

        setTransform(position, Boolean(instant));
        // Same translate (omitEnd clamp): no transitionend → unlock immediately.
        if (!instant && track.style.transform === previousTransform) {
            setAnimating(false);
        } else {
            setAnimating(!instant);
        }
        updateButtons();
        emitSelect(previousIndex);
    }

    function scrollBy(direction, event)
    {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (destroyed || isAnimating) {
            return;
        }

        if (!hasLoop) {
            const pages = getPageStarts();
            const pageIndex = getPageIndexForPosition(currentIndex);
            let targetIndex = currentIndex;

            if (direction > 0) {
                targetIndex = pages[Math.min(pageIndex + 1, pages.length - 1)];
            } else if (pages[pageIndex] === currentIndex) {
                targetIndex = pages[Math.max(0, pageIndex - 1)];
            } else {
                targetIndex = pages[pageIndex];
            }

            if (targetIndex === currentIndex) {
                return;
            }
            scrollTo(targetIndex, false);
            return;
        }

        setAnimating(true);
        const previousIndex = currentIndex;
        position += direction;
        setTransform(position, false);

        let provisional = previousIndex + direction;
        if (provisional < 0) provisional = slideCount - 1;
        if (provisional >= slideCount) provisional = 0;
        updatePagination(previousIndex, provisional);
        if (thumbs.length) {
            thumbs.forEach(function(thumb, index) {
                thumb.classList.toggle('active', index === provisional);
            });
        }
    }

    function scrollPrev(event)
    {
        scrollBy(-1, event);
    }

    function scrollNext(event)
    {
        scrollBy(1, event);
    }

    function setAllowSwipe(value)
    {
        allowSwipe = Boolean(value);
        applyTouchAction();
    }

    /**
     * While swipe is on, the browser keeps vertical scrolling and leaves horizontal pans to us.
     * Without it the browser also pans horizontally, shifting the page and triggering the
     * back/forward history gesture on mobile.
     */
    function applyTouchAction()
    {
        track.style.touchAction = allowSwipe ? 'pan-y' : '';
    }

    function onTransitionEnd(event)
    {
        if (event.target !== track || event.propertyName !== 'transform') {
            return;
        }
        const previousIndex = currentIndex;
        normalizePosition();
        setAnimating(false);
        updateButtons();
        if (previousIndex !== currentIndex) {
            emitSelect(previousIndex);
        }
    }

    function onTouchStart(event)
    {
        if (!event.touches || !event.touches.length) {
            return;
        }
        startDrag(event.touches[0].clientX, event.touches[0].clientY, false);
    }

    /**
     * @returns {boolean} whether a drag started
     */
    function startDrag(x, y, byMouse)
    {
        if (!allowSwipe || destroyed || isAnimating) {
            return false;
        }

        touchStartX = x;
        touchStartY = y;
        touchDeltaX = 0;
        swipeAxis = null;
        dragByMouse = byMouse;
        isSwiping = true;
        track.classList.add('is-dragging');
        return true;
    }

    function canDrag()
    {
        if (!hasLoop && getMaxIndex() === 0) {
            return false;
        }
        return hasLoop || slideCount > Math.ceil(getSlidesPerView());
    }

    function onTouchMove(event)
    {
        if (!event.touches || !event.touches.length) {
            return;
        }
        moveDrag(event.touches[0].clientX, event.touches[0].clientY);
    }

    function moveDrag(x, y)
    {
        if (!isSwiping) {
            return;
        }
        const deltaX = x - touchStartX;
        const deltaY = y - touchStartY;

        if (swipeAxis === null) {
            if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < AXIS_LOCK_DISTANCE) {
                return;
            }
            swipeAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
            // Checked here, not on press, so a plain click/tap never reads layout.
            if (swipeAxis === 'x' && !canDrag()) {
                swipeAxis = 'y';
            }
        }

        if (swipeAxis === 'y') {
            // Vertical page scroll: leave the carousel where it is.
            onTouchCancel();
            return;
        }

        touchDeltaX = deltaX;
        setTransform(position, false, touchDeltaX);
    }

    function onTouchEnd()
    {
        if (!isSwiping) {
            return;
        }

        isSwiping = false;
        track.classList.remove('is-dragging');

        if (touchDeltaX === 0) {
            // A plain click/tap: the track never moved, so there is nothing to snap or animate.
            touchStartX = 0;
            return;
        }
        // Force reflow so the next transform animates (is-dragging disables transition).
        void track.offsetHeight;

        if (Math.abs(touchDeltaX) > config.swipeThreshold) {
            // A mouse drag always ends in a click on whatever is under the cursor.
            if (config.suppressClickOnSwipe || dragByMouse) {
                suppressClickUntil = Date.now() + SWIPE_CLICK_WINDOW;
            }
            const indexBefore = currentIndex;
            scrollBy(touchDeltaX > 0 ? -1 : 1);
            // At the omitEnd edge scrollBy is a no-op: snap back or the track stays stuck.
            if (currentIndex === indexBefore && !isAnimating) {
                setTransform(position, false);
            }
        } else {
            setTransform(position, false);
        }

        touchStartX = 0;
        touchDeltaX = 0;
    }

    function onTouchCancel()
    {
        if (!isSwiping) {
            return;
        }
        isSwiping = false;
        track.classList.remove('is-dragging');
        setTransform(position, false);
        touchStartX = 0;
        touchDeltaX = 0;
    }

    /**
     * Mouse drag (desktop): the same gesture as a touch swipe. The move/up listeners are only on
     * window while a drag is in progress, so an idle carousel adds no handlers to mouse moves.
     */
    function onPointerDown(event)
    {
        if (event.pointerType !== 'mouse' || event.button !== 0) {
            return;
        }
        // A zoomed image pans with the mouse; dragging it must not change slide.
        if (event.target.closest && event.target.closest('.zoomed')) {
            return;
        }
        if (!startDrag(event.clientX, event.clientY, true)) {
            return;
        }
        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
        window.addEventListener('pointercancel', onPointerCancel);
        // No ghost image of the link/img under the cursor, and no text selection while dragging.
        track.addEventListener('dragstart', preventDefault);
        track.addEventListener('selectstart', preventDefault);
    }

    function onPointerMove(event)
    {
        // Released outside the window: no pointerup reached us, so end the drag here.
        if ((event.buttons & 1) === 0) {
            onPointerUp();
            return;
        }
        moveDrag(event.clientX, event.clientY);
    }

    function onPointerUp()
    {
        removeMouseDragListeners();
        onTouchEnd();
    }

    function onPointerCancel()
    {
        removeMouseDragListeners();
        onTouchCancel();
    }

    function removeMouseDragListeners()
    {
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerup', onPointerUp);
        window.removeEventListener('pointercancel', onPointerCancel);
        track.removeEventListener('dragstart', preventDefault);
        track.removeEventListener('selectstart', preventDefault);
    }

    function preventDefault(event)
    {
        event.preventDefault();
    }

    function onClickCapture(event)
    {
        // Only the click a browser may fire right after a drag. A swipe on a touch screen fires
        // none, so a plain flag would stay set and swallow the user's next real tap.
        if (Date.now() > suppressClickUntil) {
            return;
        }
        event.preventDefault();
        event.stopPropagation();
        suppressClickUntil = 0;
    }

    function onThumbnailClick(event)
    {
        const thumb = event.currentTarget;
        event.preventDefault();
        scrollTo(Number(thumb.dataset.slideIndex) || 0, false);
    }

    function onPaginationClick(event)
    {
        const item = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        scrollTo(Number(item.dataset.slideIndex) || 0, false);
    }

    function onResize()
    {
        if (destroyed) {
            return;
        }

        const previousLoop = hasLoop;
        setupClones();

        if (previousLoop !== hasLoop) {
            currentIndex = Math.min(currentIndex, hasLoop ? slideCount - 1 : getMaxIndex());
        } else if (!hasLoop) {
            currentIndex = Math.min(currentIndex, getMaxIndex());
        }

        position = hasLoop
            ? (config.loopStrategy === 'set' ? slideCount + currentIndex : currentIndex + 1)
            : currentIndex;

        setTransform(position, true);
        updateButtons();
        updateThumbnails();
    }

    function scheduleResize()
    {
        if (destroyed) {
            return;
        }
        if (resizeFrame) {
            cancelAnimationFrame(resizeFrame);
        }
        resizeFrame = requestAnimationFrame(function() {
            resizeFrame = null;
            onResize();
        });
    }

    function bind()
    {
        if (prevButton) prevButton.addEventListener('click', scrollPrev);
        if (nextButton) nextButton.addEventListener('click', scrollNext);

        applyTouchAction();
        track.addEventListener('transitionend', onTransitionEnd);
        track.addEventListener('touchstart', onTouchStart, {passive: true});
        track.addEventListener('touchmove', onTouchMove, {passive: true});
        track.addEventListener('touchend', onTouchEnd);
        track.addEventListener('touchcancel', onTouchCancel);
        track.addEventListener('pointerdown', onPointerDown);

        root.addEventListener('click', onClickCapture, true);

        thumbs.forEach(function(thumb) {
            thumb.addEventListener('click', onThumbnailClick);
        });

        if (paginator) {
            paginator.querySelectorAll(selectors.paginationItem).forEach(function(item) {
                item.addEventListener('click', onPaginationClick);
            });
        }

        if (config.watchResize) {
            window.addEventListener('resize', onResize);
            if (typeof ResizeObserver !== 'undefined' && viewport) {
                resizeObserver = new ResizeObserver(scheduleResize);
                resizeObserver.observe(viewport);
            }
        }
    }

    function destroy()
    {
        if (destroyed) {
            return;
        }
        destroyed = true;

        if (prevButton) prevButton.removeEventListener('click', scrollPrev);
        if (nextButton) nextButton.removeEventListener('click', scrollNext);

        track.removeEventListener('transitionend', onTransitionEnd);
        track.removeEventListener('touchstart', onTouchStart);
        track.removeEventListener('touchmove', onTouchMove);
        track.removeEventListener('touchend', onTouchEnd);
        track.removeEventListener('touchcancel', onTouchCancel);
        track.removeEventListener('pointerdown', onPointerDown);
        removeMouseDragListeners();
        track.style.touchAction = '';

        root.removeEventListener('click', onClickCapture, true);

        thumbs.forEach(function(thumb) {
            thumb.removeEventListener('click', onThumbnailClick);
        });

        if (paginator) {
            paginator.querySelectorAll(selectors.paginationItem).forEach(function(item) {
                item.removeEventListener('click', onPaginationClick);
            });
        }

        if (config.watchResize) {
            window.removeEventListener('resize', onResize);
            if (resizeObserver) {
                resizeObserver.disconnect();
                resizeObserver = null;
            }
            if (resizeFrame) {
                cancelAnimationFrame(resizeFrame);
                resizeFrame = null;
            }
        }

        clearAnimationUnlockTimer();
        isAnimating = false;
        clearClones();
        root.classList.remove('is-initialized');
    }

    function reInit()
    {
        onResize();
    }

    setupClones();
    bind();
    setTransform(position, true);
    updateButtons();
    updateThumbnails();
    root.classList.add('is-initialized');

    const api = {
        scrollTo: scrollTo,
        scrollPrev: scrollPrev,
        scrollNext: scrollNext,
        getIndex: function() {
            return currentIndex;
        },
        getSlideCount: function() {
            return slideCount;
        },
        setAllowSwipe: setAllowSwipe,
        reInit: reInit,
        destroy: destroy
    };

    if (typeof config.onInit === 'function') {
        config.onInit(api);
    }

    return api;
}

function resolveThumbnails(root, thumbnails)
{
    if (!thumbnails) {
        return [];
    }
    if (typeof thumbnails === 'string') {
        const scope = root.closest('.modal') || root.parentElement || document;
        return Array.from(scope.querySelectorAll(thumbnails));
    }
    if (thumbnails.length !== undefined) {
        return Array.from(thumbnails);
    }
    return [];
}

function clamp(value, min, max)
{
    return Math.max(min, Math.min(value, max));
}
